/**
 * Multi-Client Brand Theme System
 * 
 * Type definitions for client-specific theme customization.
 * All theme properties are optional with sensible defaults.
 */

export interface ClientThemeColors {
  /** App shell background (maps to `--background`) — flat runtime form */
  background?: string;
  /** App shell foreground text (maps to `--foreground`) */
  foreground?: string;

  /** Primary brand color (e.g., "#8b5cf6") */
  primary?: string;
  /** Text color on primary background */
  primaryForeground?: string;
  /** Darker variant of primary for headers/backgrounds (light mode) */
  primaryDark?: string;
  /** Text and icons on header chrome (`--primary-dark`) */
  primaryDarkForeground?: string;
  
  /** Secondary color */
  secondary?: string;
  /** Text color on secondary background */
  secondaryForeground?: string;
  
  /** Accent color */
  accent?: string;
  /** Text color on accent background */
  accentForeground?: string;
  
  /** Destructive/error color */
  destructive?: string;
  /** Text color on destructive background */
  destructiveForeground?: string;
  
  /** Muted/subtle color */
  muted?: string;
  /** Text color on muted background */
  mutedForeground?: string;

  /** Success / positive state */
  success?: string;
  successForeground?: string;

  /** Warning / caution state */
  warning?: string;
  warningForeground?: string;

  /** Promoted / featured listings (job board chips) */
  promotion?: string;
  promotionForeground?: string;

  /** Informational state */
  info?: string;
  infoForeground?: string;
  
  /** Sidebar background (e.g. "#ffffff") */
  sidebar?: string;

  /** Text and icon color on the sidebar rail */
  sidebarForeground?: string;

  /** Sidebar nav hover / active fill (--sidebar-accent) */
  sidebarAccent?: string;
  /** Text on sidebar accent surfaces */
  sidebarAccentForeground?: string;
  
  /** Page content area background (right of chat only) */
  pageBackground?: string;
  /** Text color in page content area */
  pageForeground?: string;

  /** Card / raised content background */
  card?: string;
  /** Text color on card surfaces */
  cardForeground?: string;

  /** Popover, dropdown, and menu background */
  popover?: string;
  /** Text color on popover surfaces */
  popoverForeground?: string;

  /** Border color (--border) */
  border?: string;
  /** Input field border/fill (--input) */
  input?: string;
  /** Skeleton loading placeholder (--skeleton) */
  skeleton?: string;
}

export interface ClientThemeTypography {
  fontFamily?: {
    /** Sans-serif font stack */
    sans?: string;
    /** Serif font stack */
    serif?: string;
    /** Monospace font stack */
    mono?: string;
    /** Wordmark / display brand font stack */
    brand?: string;
  };
}

export interface ClientThemeBorderRadius {
  /** Base border radius (e.g., "0.5rem") */
  base?: string;
  /** Button border radius; defaults to 0 (see --radius-button) */
  button?: string;
}

export interface ClientTheme {
  /** Color palette customization */
  colors?: ClientThemeColors;
  
  /** Typography customization */
  typography?: ClientThemeTypography;
  
  /** Border radius customization */
  borderRadius?: ClientThemeBorderRadius;
  
  /** Dark mode overrides (optional) */
  dark?: {
    colors?: ClientThemeColors;
  };
}

export type SidebarMenuSelectedStyle = "boxed" | "text" | "accent" | "underline" | "minimal";
export type { SidebarNavFocusConfig, SidebarNavFocusKey, SidebarNavItemConfig } from "@/types/sidebar-nav";

/** Sidebar rail brand mark presentation (Labs chrome). */
export type SidebarBrandVariant = "default" | "yeshumanMark";
export type SidebarBrandSize = "md" | "lg" | "xl";

export interface SidebarBrandConfig {
  /** `default` = logo → favicon → Lucide; `yeshumanMark` = full stick-figure-in-circle. */
  variant?: SidebarBrandVariant;
  /** Hit/mark size: md 32, lg 40 (+ wider rail), xl 48 (+ wider rail). Default md. */
  size?: SidebarBrandSize;
  /** When true (default), wrap in muted rounded square. */
  boxed?: boolean;
}

export interface ClientLayout {
  /** Mobile only. Omitted/panes retains the existing chat/page panes; drawer opts into floating Ask AI. */
  mobileAgentPresentation?: 'panes' | 'drawer' | 'swipe';
  /** Drawer launcher only; defaults to the floating button. */
  mobileAgentLauncher?: 'button' | 'composer';
  /** Move Threads and New chat into a separated mobile sidebar group. Default retains their existing location. */
  mobileChatActionsPlacement?: 'default' | 'menu';
  /** Optional mobile wordmark width in CSS pixels. Desktop retains its existing sizing. */
  mobileHeaderLogoWidth?: number;
  /** Mobile Invite location; defaults to the header. */
  mobileInvitePlacement?: 'header' | 'menu' | 'settings-menu';
  /** Mobile notification entry location; desktop always retains its bell. */
  mobileNotificationsPlacement?: 'header' | 'settings-menu';
  headerFullWidth?: boolean;
  sidebarDefaultOpen?: boolean;
  sidebarMenuStyle?: "horizontal" | "vertical";
  sidebarMenuSelectedStyle?: SidebarMenuSelectedStyle;
  /** When true, a new empty thread uses welcome empty-chat instead of the in-chat thread dock. */
  newChatCentered?: boolean;
  showFocusSwitching?: boolean;
  memoriesThreadsInHeader?: boolean;
  contentInset?: boolean;
  hideThreadsList?: boolean;
  sidebarCollapsible?: boolean;
  disableSidebarKeyboardShortcut?: boolean;
  hideSidebarLogo?: boolean;
  headerBackgroundPrimary?: boolean;
  headerLogoColor?: string;
  /** Show a light right border on the sidebar */
  sidebarRightBorder?: boolean;
  /** Chat thread presentation: classic (avatars + bubbles) or document (user bubble only, assistant prose). */
  chatMessageStyle?: 'classic' | 'document';
  /** User avatar in classic chat: icon (default) or profile photo. */
  chatUserAvatar?: 'icon' | 'profile';
  /** Optional override for sidebar rail brand mark chrome. */
  sidebarBrand?: SidebarBrandConfig;
  /**
   * UI icon geometry for this tenant’s Labs Vite build.
   * `sketchy` aliases `lucide-react` → `@sketchyicons/react` (all Lucide imports in that deploy).
   */
  iconStyle?: 'default' | 'sketchy';
  /** List filter bar primary/secondary key overrides per list surface. */
  listFilterLayouts?: import('@/components/filters/list-filter-types').ListFilterLayoutConfig;
  /** @deprecated Both minimal and expanded now use ListFilterBar; kept for compat. */
  filtersUi?: 'minimal' | 'expanded';
}

/**
 * Default theme that all clients inherit from.
 * Matches the current values in index.css
 */
export const DEFAULT_THEME: ClientTheme = {
  colors: {
    primary: "#3b82f6", // Blue
    primaryForeground: "#ffffff",
    secondary: "#f3f4f6",
    secondaryForeground: "#111827",
    accent: "#f3f4f6",
    accentForeground: "#111827",
    destructive: "#ef4444",
    destructiveForeground: "#ffffff",
    muted: "#f3f4f6",
    mutedForeground: "#6b7280",
    success: "#059669",
    successForeground: "#ffffff",
    warning: "#d97706",
    warningForeground: "#ffffff",
    promotion: "#FFD110",
    promotionForeground: "#6B4E00",
    info: "#2563eb",
    infoForeground: "#ffffff",
  },
  typography: {
    fontFamily: {
      sans: "Nunito, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, sans-serif",
    },
  },
  borderRadius: {
    base: "0.625rem",
    button: "0",
  },
};

/**
 * Deep merge utility for theme objects
 */
export function mergeTheme(clientTheme?: ClientTheme, defaultTheme: ClientTheme = DEFAULT_THEME): ClientTheme {
  if (!clientTheme) {
    return defaultTheme;
  }

  return {
    colors: {
      ...defaultTheme.colors,
      ...clientTheme.colors,
    },
    typography: {
      ...defaultTheme.typography,
      ...clientTheme.typography,
      fontFamily: {
        ...defaultTheme.typography?.fontFamily,
        ...clientTheme.typography?.fontFamily,
      },
    },
    borderRadius: {
      ...defaultTheme.borderRadius,
      ...clientTheme.borderRadius,
    },
    dark: {
      colors: {
        ...defaultTheme.dark?.colors,
        ...clientTheme.dark?.colors,
      },
    },
  };
}

/**
 * Convert color to oklch format for CSS variables.
 * Uses lookup table for common colors; unknown hex passes through unchanged (valid in CSS vars).
 */
const COMMON_COLOR_TO_OKLCH: Record<string, string> = {
  '#3b82f6': 'oklch(0.488 0.243 264.376)',
  '#2563eb': 'oklch(0.439 0.227 264.376)',
  '#60a5fa': 'oklch(0.667 0.15 250)',
  '#1e40af': 'oklch(0.369 0.211 264.376)',
  '#1e3a8a': 'oklch(0.307 0.15 264.376)',
  '#00ff41': 'oklch(0.82 0.28 150)',
  '#00ff88': 'oklch(0.87 0.25 165)',
  '#ffffff': 'oklch(1 0 0)',
  '#f3f4f6': 'oklch(0.97 0 0)',
  '#111827': 'oklch(0.205 0 0)',
  '#ef4444': 'oklch(0.577 0.245 27.325)',
  '#6b7280': 'oklch(0.556 0 0)',
  '#1e3a5f': 'oklch(0.241 0.253 276.2)',
  '#051e38': 'oklch(0.18 0.08 250)',
  '#0b5cff': 'oklch(0.546 0.251 262.481)',
  '#5993ff': 'oklch(0.676 0.171 261.665)',
  '#05173f': 'oklch(0.219 0.081 262.602)',
  '#1847a5': 'oklch(0.42 0.14 260.5)',
  '#10b981': 'oklch(0.696 0.17 162.48)',
  '#34d399': 'oklch(0.765 0.16 163.22)',
  '#064e3b': 'oklch(0.307 0.08 170.7)',
  '#f59e0b': 'oklch(0.769 0.17 75.83)',
  '#fbbf24': 'oklch(0.828 0.16 84.43)',
  '#78350f': 'oklch(0.344 0.1 55.7)',
  '#8b5cf6': 'oklch(0.606 0.25 292.72)',
  '#a78bfa': 'oklch(0.702 0.18 292.79)',
  '#4c1d95': 'oklch(0.319 0.21 297.1)',
  '#f8fafc': 'oklch(0.985 0.002 247.858)',
  '#f1f5f9': 'oklch(0.969 0.007 247.896)',
  '#0f172a': 'oklch(0.129 0.042 264.695)',
  '#1e293b': 'oklch(0.21 0.04 264.695)',
  '#059669': 'oklch(0.527 0.154 150.069)',
  '#d97706': 'oklch(0.666 0.157 58.318)',
};

export function convertColorToOklch(color: string): string {
  if (typeof color !== 'string' || !color.trim()) {
    return '';
  }

  if (color.startsWith('oklch(')) {
    return color;
  }

  if (color.startsWith('rgb')) {
    return color;
  }

  const normalizedColor = color.toLowerCase().trim();
  if (COMMON_COLOR_TO_OKLCH[normalizedColor]) {
    return COMMON_COLOR_TO_OKLCH[normalizedColor];
  }

  if (normalizedColor.startsWith('#')) {
    return normalizedColor;
  }

  return color;
}
